import React, { useState, useEffect, useRef } from 'react';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';
import TradingModal from '../../../components/TradingModal';
import Select from '../../../components/ui/Select';
import tradingApi from '../../../services/tradingApi';
import useLanguage from '../../../hooks/useLanguage';

// Technical Indicators Calculations
const calculateSMA = (data, period) => {
  const result = [];
  for (let i = 0; i < data?.length; i++) {
    if (i < period - 1) {
      result?.push(null);
    } else {
      const sum = data?.slice(i - period + 1, i + 1)?.reduce((acc, val) => acc + val?.close, 0);
      result?.push(sum / period);
    }
  }
  return result;
};

const calculateEMA = (data, period) => {
  const result = [];
  const multiplier = 2 / (period + 1);
  let ema = data?.[0]?.close;
  
  for (let i = 0; i < data?.length; i++) {
    if (i === 0) {
      result?.push(ema);
    } else {
      ema = (data?.[i]?.close * multiplier) + (ema * (1 - multiplier));
      result?.push(ema);
    }
  }
  return result;
};

const calculateRSI = (data, period = 14) => {
  const result = [];
  const gains = [];
  const losses = [];
  
  for (let i = 1; i < data?.length; i++) {
    const change = data?.[i]?.close - data?.[i - 1]?.close;
    gains?.push(Math.max(change, 0));
    losses?.push(Math.abs(Math.min(change, 0)));
  }
  
  for (let i = 0; i < gains?.length; i++) {
    if (i < period - 1) {
      result?.push(null);
    } else {
      const avgGain = gains?.slice(i - period + 1, i + 1)?.reduce((a, b) => a + b, 0) / period;
      const avgLoss = losses?.slice(i - period + 1, i + 1)?.reduce((a, b) => a + b, 0) / period;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      const rsi = 100 - (100 / (1 + rs));
      result?.push(rsi);
    }
  }
  
  return [null, ...result]; // Adjust for the initial shift
};

const calculateBollingerBands = (data, period = 20, multiplier = 2) => {
  const sma = calculateSMA(data, period);
  const upperBand = [];
  const lowerBand = [];
  
  for (let i = 0; i < data?.length; i++) {
    if (i < period - 1) {
      upperBand?.push(null);
      lowerBand?.push(null);
    } else {
      const slice = data?.slice(i - period + 1, i + 1);
      const mean = sma?.[i];
      const variance = slice?.reduce((acc, val) => acc + Math.pow(val?.close - mean, 2), 0) / period;
      const stdDev = Math.sqrt(variance);
      upperBand?.push(mean + (stdDev * multiplier));
      lowerBand?.push(mean - (stdDev * multiplier));
    }
  }
  
  return { sma, upperBand, lowerBand };
};

// Modified Flat Horizontal Trend Line Function
const calculateFlatTrendLine = (data, lookbackPeriod = 20) => {
  const result = [];
  
  for (let i = 0; i < data?.length; i++) {
    if (i < lookbackPeriod - 1) {
      result?.push(null);
    } else {
      // Get the last 'lookbackPeriod' candles
      const recentData = data?.slice(i - lookbackPeriod + 1, i + 1);
      
      // Calculate average price for flat horizontal trend
      const avgPrice = recentData?.reduce((sum, candle) => sum + candle?.close, 0) / recentData?.length;
      
      // Return flat horizontal line at average price
      result?.push(avgPrice);
    }
  }
  
  return result;
};

// Modified Dynamic Trend Line - Flat Horizontal
const calculateFlatDynamicTrendLine = (data, period = 10) => {
  if (!data || data?.length < period) return [];
  
  const result = [];
  const lastCandles = data?.slice(-period);
  
  // Calculate average of the last 'period' candles for flat line
  const avgPrice = lastCandles?.reduce((sum, candle) => sum + candle?.close, 0) / lastCandles?.length;
  
  // Generate flat horizontal line values for all data points
  for (let i = 0; i < data?.length; i++) {
    if (i < data?.length - period) {
      result?.push(null);
    } else {
      // Flat horizontal line at average price
      result?.push(avgPrice);
    }
  }
  
  return result;
};

// Custom Candlestick Component for Recharts
const CandlestickBar = ({ payload, x, y, width, height }) => {
  if (!payload) return null;
  
  const { open, high, low, close } = payload;
  const isPositive = close >= open;
  const color = isPositive ? '#22c55e' : '#ef4444';
  
  // Calculate candlestick dimensions
  const bodyHeight = Math.abs(close - open);
  const bodyY = Math.min(open, close);
  const wickTop = high;
  const wickBottom = low;
  
  // Scale calculations (assuming the chart scales properly)
  const priceRange = high - low;
  if (priceRange === 0) return null;
  
  const scaleRatio = height / priceRange;
  const scaledBodyHeight = bodyHeight * scaleRatio;
  const scaledBodyY = y + (high - bodyY) * scaleRatio;
  const scaledWickTop = y;
  const scaledWickBottom = y + height;
  
  return (
    <g>
      {/* High-Low Wick */}
      <line
        x1={x + width / 2}
        y1={scaledWickTop}
        x2={x + width / 2}
        y2={scaledWickBottom}
        stroke={color}
        strokeWidth={1}
      />
      {/* Open-Close Body */}
      <rect
        x={x + width * 0.2}
        y={scaledBodyY}
        width={width * 0.6}
        height={Math.max(scaledBodyHeight, 1)}
        fill={isPositive ? color : color}
        fillOpacity={isPositive ? 0.3 : 1}
        stroke={color}
        strokeWidth={1}
      />
    </g>
  );
};

const TradingChart = ({ selectedSymbol }) => {
  const chartRef = useRef(null);
  const wsRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const [currentSymbol, setCurrentSymbol] = useState(selectedSymbol || { symbol: 'EURUSD', bid: 1.1674, ask: 1.1676 });
  const [chartTimeframe, setChartTimeframe] = useState('1H');
  const [tradingModalOpen, setTradingModalOpen] = useState(false);
  const [tradingType, setTradingType] = useState('buy');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [chartData, setChartData] = useState([]);
  const [chartLoaded, setChartLoaded] = useState(false);
  const [ohlcData, setOhlcData] = useState([]);
  
  // Indicator states
  const [selectedIndicators, setSelectedIndicators] = useState(['SMA20', 'SMA50']);
  const [indicatorPanelOpen, setIndicatorPanelOpen] = useState(false);
  const [chartDataWithIndicators, setChartDataWithIndicators] = useState([]);
  
  const { t, isRTL, forceUpdate } = useLanguage();

  // Available indicators
  const availableIndicators = [
    { key: 'SMA20', name: t('sma20') || 'SMA 20', type: 'overlay', color: '#3b82f6' },
    { key: 'SMA50', name: t('sma50') || 'SMA 50', type: 'overlay', color: '#ef4444' },
    { key: 'EMA12', name: t('ema12') || 'EMA 12', type: 'overlay', color: '#10b981' },
    { key: 'EMA26', name: t('ema26') || 'EMA 26', type: 'overlay', color: '#f59e0b' },
    { key: 'RSI14', name: t('rsi14') || 'RSI 14', type: 'oscillator', color: '#8b5cf6' },
    { key: 'BB20', name: t('bollingerBands') || 'Bollinger Bands', type: 'overlay', color: '#6b7280' },
    { key: 'TRENDLINE', name: t('trendLine') || 'Trend Çizgisi', type: 'overlay', color: '#ff6b35' },
    { key: 'DYNAMIC_TREND', name: t('dynamicTrend') || 'Dinamik Trend', type: 'overlay', color: '#00d2ff' }
  ];

  // Force re-render when language changes
  useEffect(() => {
    // Component will re-render when forceUpdate changes
  }, [forceUpdate]);

  useEffect(() => {
    if (selectedSymbol) {
      setCurrentSymbol(selectedSymbol);
    }
  }, [selectedSymbol]);

  // Calculate indicators when chart data changes
  useEffect(() => {
    if (chartData?.length > 0) {
      const dataWithIndicators = chartData?.map((item, index) => {
        const newItem = { ...item };
        
        // Calculate SMA indicators
        if (selectedIndicators?.includes('SMA20')) {
          const sma20 = calculateSMA(chartData, 20);
          newItem.sma20 = sma20?.[index];
        }
        
        if (selectedIndicators?.includes('SMA50')) {
          const sma50 = calculateSMA(chartData, 50);
          newItem.sma50 = sma50?.[index];
        }
        
        // Calculate EMA indicators
        if (selectedIndicators?.includes('EMA12')) {
          const ema12 = calculateEMA(chartData, 12);
          newItem.ema12 = ema12?.[index];
        }
        
        if (selectedIndicators?.includes('EMA26')) {
          const ema26 = calculateEMA(chartData, 26);
          newItem.ema26 = ema26?.[index];
        }
        
        // Calculate RSI indicator
        if (selectedIndicators?.includes('RSI14')) {
          const rsi14 = calculateRSI(chartData, 14);
          newItem.rsi14 = rsi14?.[index];
        }
        
        // Calculate Bollinger Bands
        if (selectedIndicators?.includes('BB20')) {
          const bb = calculateBollingerBands(chartData, 20, 2);
          newItem.bbUpper = bb?.upperBand?.[index];
          newItem.bbMiddle = bb?.sma?.[index];
          newItem.bbLower = bb?.lowerBand?.[index];
        }
        
        // Calculate Flat Horizontal Trend Line
        if (selectedIndicators?.includes('TRENDLINE')) {
          const trendLine = calculateFlatTrendLine(chartData, 20);
          newItem.trendLine = trendLine?.[index];
        }
        
        // Calculate Flat Dynamic Trend Line
        if (selectedIndicators?.includes('DYNAMIC_TREND')) {
          const dynamicTrend = calculateFlatDynamicTrendLine(chartData, 10);
          newItem.dynamicTrend = dynamicTrend?.[index];
        }
        
        return newItem;
      });
      
      setChartDataWithIndicators(dataWithIndicators);
    }
  }, [chartData, selectedIndicators]);

  // Initialize Chart Data and WebSocket Connection
  useEffect(() => {
    initializeChart();
    connectToWebSocket();

    return () => {
      if (unsubscribeRef?.current) {
        unsubscribeRef?.current();
      }
      tradingApi?.disconnectWebSocket();
    };
  }, [currentSymbol?.symbol, chartTimeframe]);

  const initializeChart = async () => {
    try {
      setChartLoaded(false);
      
      // Try to load historical OHLC data from API
      const ohlcData = await tradingApi?.getOHLCData(
        currentSymbol?.symbol || 'EURUSD', 
        chartTimeframe, 
        100
      );
      
      if (ohlcData && ohlcData?.length > 0) {
        // Process API data for Recharts format
        const processedData = ohlcData?.map((candle, index) => ({
          time: new Date(candle?.timestamp)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          timestamp: candle?.timestamp,
          open: parseFloat(candle?.open),
          high: parseFloat(candle?.high),
          low: parseFloat(candle?.low),
          close: parseFloat(candle?.close),
          volume: candle?.volume || 0,
          isPositive: candle?.close >= candle?.open,
          bodyHeight: Math.abs(candle?.close - candle?.open),
          wickLength: candle?.high - candle?.low
        }));
        
        setChartData(processedData);
        console.log('Chart data loaded from API:', processedData?.length, 'candles');
      } else {
        throw new Error('No OHLC data received from API');
      }
      
      setChartLoaded(true);
    } catch (apiError) {
      console.warn('API OHLC data loading failed, using mock data:', apiError);
      
      // Fallback to mock data
      const mockData = generateMockOHLCData();
      setChartData(mockData);
      setChartLoaded(true);
    }
  };

  // Subscribe to WebSocket for chart data updates
  useEffect(() => {
    const unsubscribe = tradingApi?.subscribe('ohlc', selectedSymbol?.symbol, (data) => {
      console.log('Received chart data via WebSocket:', data);
      
      // Handle chart array data from WebSocket response
      if (data?.chart && Array.isArray(data?.chart)) {
        const formattedData = data?.chart?.map(item => ({
          timestamp: item?.timestamp || item?.time || Date.now(),
          open: parseFloat(item?.open) || 0,
          high: parseFloat(item?.high) || 0,
          low: parseFloat(item?.low) || 0,
          close: parseFloat(item?.close) || 0,
          volume: parseInt(item?.volume) || 0
        }));
        
        setOhlcData(prev => {
          // Merge new data with existing data
          const combined = [...prev, ...formattedData];
          // Sort by timestamp and remove duplicates
          const sorted = combined?.sort((a, b) => a?.timestamp - b?.timestamp);
          const unique = sorted?.filter((item, index, arr) => 
            index === 0 || item?.timestamp !== arr?.[index - 1]?.timestamp
          );
          // Keep only the last 500 candles for performance
          return unique?.slice(-500);
        });
        
        console.log('Updated chart with', formattedData?.length, 'new candles');
      }
      
      // Handle single OHLC updates
      if (data?.type === 'ohlc_update' || data?.type === 'chart') {
        const newCandle = {
          timestamp: data?.timestamp || Date.now(),
          open: parseFloat(data?.open) || 0,
          high: parseFloat(data?.high) || 0,
          low: parseFloat(data?.low) || 0,
          close: parseFloat(data?.close) || 0,
          volume: parseInt(data?.volume) || 0
        };
        
        setOhlcData(prev => {
          const updated = [...prev];
          const lastIndex = updated?.length - 1;
          
          if (lastIndex >= 0 && Math.abs(updated?.[lastIndex]?.timestamp - newCandle?.timestamp) < 60000) {
            // Update last candle if within same minute
            updated[lastIndex] = newCandle;
          } else {
            // Add new candle
            updated?.push(newCandle);
          }
          
          return updated?.slice(-500); // Keep last 500 candles
        });
      }
    });

    return unsubscribe;
  }, [selectedSymbol]);

  const handleOHLCUpdate = (ohlcData) => {
    try {
      const newCandle = {
        time: new Date(ohlcData?.timestamp)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        timestamp: ohlcData?.timestamp,
        open: parseFloat(ohlcData?.open),
        high: parseFloat(ohlcData?.high),
        low: parseFloat(ohlcData?.low),
        close: parseFloat(ohlcData?.close),
        volume: ohlcData?.volume || 0,
        isPositive: ohlcData?.close >= ohlcData?.open,
        bodyHeight: Math.abs(ohlcData?.close - ohlcData?.open),
        wickLength: ohlcData?.high - ohlcData?.low
      };

      setChartData(prev => {
        const updated = [...prev?.slice(-99), newCandle];
        return updated;
      });
      
      // Update symbol prices
      setCurrentSymbol(prev => ({
        ...prev,
        bid: ohlcData?.close - 0.0001,
        ask: ohlcData?.close + 0.0001,
        change: ohlcData?.close - (prev?.bid || ohlcData?.close),
        changePercent: ((ohlcData?.close - (prev?.bid || ohlcData?.close)) / (prev?.bid || ohlcData?.close)) * 100
      }));
      
    } catch (error) {
      console.error('Error handling OHLC update:', error);
    }
  };

  const connectToWebSocket = () => {
    try {
      setConnectionStatus('connecting');
      
      // Connect to Zonex WebSocket for real-time chart data
      tradingApi?.connectWebSocket(
        (data) => handleWebSocketMessage(data),
        (error) => {
          console.error('WebSocket error:', error);
          setConnectionStatus('error');
          simulateConnection(); // Fallback to simulation
        },
        (event) => {
          console.log('WebSocket closed:', event);
          setConnectionStatus('disconnected');
        }
      );

      // Wait for connection and handle chart data subscription
      setTimeout(() => {
        if (tradingApi?.getConnectionStatus() === 'connected') {
          setConnectionStatus('connected');
          console.log('WebSocket connected for chart data reception');
        } else {
          // Fallback to simulated updates
          simulateConnection();
        }
      }, 1500);

    } catch (error) {
      console.error('WebSocket connection failed:', error);
      setConnectionStatus('error');
      simulateConnection();
    }
  };

  const handleWebSocketMessage = (data) => {
    switch (data?.type) {
      case 'price_update':
        if (data?.symbol === currentSymbol?.symbol) {
          setCurrentSymbol(prev => ({
            ...prev,
            bid: data?.bid,
            ask: data?.ask,
            change: data?.change,
            changePercent: data?.changePercent
          }));
        }
        break;
        
      case 'chart': case'ohlc_update':
        // Handle chart data from WebSocket payload response
        if (data?.chart && Array.isArray(data?.chart)) {
          const processedChartData = data?.chart?.map((candle, index) => ({
            time: new Date(candle?.timestamp || Date.now() - (data?.chart?.length - index) * 60000)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            timestamp: candle?.timestamp || Date.now() - (data?.chart?.length - index) * 60000,
            open: parseFloat(candle?.open) || 0,
            high: parseFloat(candle?.high) || 0,
            low: parseFloat(candle?.low) || 0,
            close: parseFloat(candle?.close) || 0,
            volume: candle?.volume || 0,
            isPositive: candle?.close >= candle?.open,
            bodyHeight: Math.abs(candle?.close - candle?.open),
            wickLength: candle?.high - candle?.low
          }));
          
          // Continuously update chart data
          setChartData(processedChartData);
          console.log('Updated chart with WebSocket data:', processedChartData?.length, 'candles');
        }
        
        // Handle single OHLC update
        if (data?.symbol === currentSymbol?.symbol && data?.timeframe === chartTimeframe) {
          handleOHLCUpdate(data);
        }
        break;
        
      case 'connection_status': setConnectionStatus(data?.status ||'connected');
        break;
        
      default:
        // Handle chart array data from WebSocket payload response
        if (data?.chart && Array.isArray(data?.chart)) {
          const processedChartData = data?.chart?.map((candle, index) => ({
            time: new Date(candle?.timestamp || Date.now() - (data?.chart?.length - index) * 60000)?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            timestamp: candle?.timestamp || Date.now() - (data?.chart?.length - index) * 60000,
            open: parseFloat(candle?.open) || 0,
            high: parseFloat(candle?.high) || 0,
            low: parseFloat(candle?.low) || 0,
            close: parseFloat(candle?.close) || 0,
            volume: candle?.volume || 0,
            isPositive: candle?.close >= candle?.open,
            bodyHeight: Math.abs(candle?.close - candle?.open),
            wickLength: candle?.high - candle?.low
          }));
          
          setChartData(processedChartData);
          console.log('Processed chart data from WebSocket:', processedChartData?.length, 'candles');
        }
        break;
    }
  };

  // Simulate connection for development when API is not available
  const simulateConnection = () => {
    setConnectionStatus('connecting');
    
    setTimeout(() => {
      setConnectionStatus('connected');
      
      // Simulate real-time updates
      const updateInterval = setInterval(() => {
        if (chartData?.length > 0) {
          const lastCandle = chartData?.[chartData?.length - 1];
          if (lastCandle) {
            const variation = (Math.random() - 0.5) * 0.001;
            const newPrice = lastCandle?.close + variation;
            
            const simulatedOHLC = {
              timestamp: Date.now(),
              symbol: currentSymbol?.symbol,
              timeframe: chartTimeframe,
              open: lastCandle?.close,
              high: Math.max(lastCandle?.close, newPrice) + Math.random() * 0.0002,
              low: Math.min(lastCandle?.close, newPrice) - Math.random() * 0.0002,
              close: newPrice,
              volume: Math.floor(Math.random() * 1000) + 500
            };
            
            handleOHLCUpdate(simulatedOHLC);
          }
        }
      }, 3000);

      return () => clearInterval(updateInterval);
    }, 1000);
  };

  // Generate mock OHLC data with proper format for Recharts
  const generateMockOHLCData = () => {
    const data = [];
    const basePrice = currentSymbol?.ask || 1.1676;
    let currentPrice = basePrice;
    const now = new Date();
    
    for (let i = 100; i >= 0; i--) {
      const time = new Date(now?.getTime() - i * 60000); // 1 minute intervals
      const variation = (Math.random() - 0.5) * 0.001;
      const open = currentPrice;
      const close = open + variation;
      const high = Math.max(open, close) + Math.random() * 0.0005;
      const low = Math.min(open, close) - Math.random() * 0.0005;
      
      data?.push({
        time: time?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        timestamp: time?.getTime(),
        open: parseFloat(open?.toFixed(5)),
        high: parseFloat(high?.toFixed(5)),
        low: parseFloat(low?.toFixed(5)),
        close: parseFloat(close?.toFixed(5)),
        volume: Math.floor(Math.random() * 10000) + 1000,
        // Additional data for the custom candlestick
        isPositive: close >= open,
        bodyHeight: Math.abs(close - open),
        wickLength: high - low
      });
      
      currentPrice = close;
    }
    
    return data;
  };

  // Custom Candlestick Shape Component
  const CustomCandlestick = (props) => {
    const { payload, x, y, width, height } = props;
    
    if (!payload?.payload) return null;
    
    const data = payload?.payload;
    const { open, high, low, close } = data;
    const isPositive = close >= open;
    const color = isPositive ? '#22c55e' : '#ef4444';
    
    // Calculate positions relative to the chart area
    const range = high - low;
    if (range === 0) return null;
    
    const bodyTop = Math.max(open, close);
    const bodyBottom = Math.min(open, close);
    const bodyHeight = Math.abs(close - open);
    
    // Position calculations
    const wickX = x + width / 2;
    const bodyX = x + width * 0.25;
    const bodyWidth = width * 0.5;
    
    // Y positions (inverted because SVG coordinates)
    const highY = y;
    const lowY = y + height;
    const bodyTopY = y + ((high - bodyTop) / range) * height;
    const bodyBottomY = y + ((high - bodyBottom) / range) * height;
    const scaledBodyHeight = Math.max(Math.abs(bodyBottomY - bodyTopY), 1);
    
    return (
      <g>
        {/* High-Low Wick */}
        <line
          x1={wickX}
          y1={highY}
          x2={wickX}
          y2={lowY}
          stroke={color}
          strokeWidth={1}
          opacity={0.8}
        />
        {/* Open-Close Body */}
        <rect
          x={bodyX}
          y={bodyTopY}
          width={bodyWidth}
          height={scaledBodyHeight}
          fill={isPositive ? 'transparent' : color}
          stroke={color}
          strokeWidth={1.5}
          fillOpacity={isPositive ? 0 : 1}
        />
      </g>
    );
  };

  // Indicator toggle function
  const toggleIndicator = (indicatorKey) => {
    setSelectedIndicators(prev => {
      if (prev?.includes(indicatorKey)) {
        return prev?.filter(key => key !== indicatorKey);
      } else {
        return [...prev, indicatorKey];
      }
    });
  };

  const timeframes = ['1M', '5M', '15M', '30M', '1H', '4H', '1D', '1W'];

  // Prepare timeframe options for Select component
  const timeframeOptions = timeframes?.map(tf => ({
    value: tf,
    label: tf
  }));

  const formatPrice = (price) => {
    if (!price) return '0.00000';
    if (price > 1000) return price?.toFixed(1);
    if (price > 10) return price?.toFixed(3);
    return price?.toFixed(5);
  };

  const handleTradeClick = (type) => {
    setTradingType(type);
    setTradingModalOpen(true);
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-500';
      case 'connecting': return 'text-yellow-500';
      case 'error': return 'text-red-500';
      default: return 'text-red-500';
    }
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected': return 'Wifi';
      case 'connecting': return 'RotateCw';
      case 'error': return 'WifiOff';
      default: return 'WifiOff';
    }
  };

  // Custom tooltip for better price display
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      const data = payload?.[0]?.payload;
      if (!data) return null;
      
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-foreground mb-2">{label}</p>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Open:</span>
              <span className="font-mono text-foreground">{formatPrice(data?.open)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">High:</span>
              <span className="font-mono text-green-600">{formatPrice(data?.high)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Low:</span>
              <span className="font-mono text-red-600">{formatPrice(data?.low)}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Close:</span>
              <span className={`font-mono ${data?.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {formatPrice(data?.close)}
              </span>
            </div>
            
            {/* Display indicator values */}
            {selectedIndicators?.map(indicator => {
              const indicatorConfig = availableIndicators?.find(ind => ind?.key === indicator);
              let value = null;
              
              switch (indicator) {
                case 'SMA20':
                  value = data?.sma20;
                  break;
                case 'SMA50':
                  value = data?.sma50;
                  break;
                case 'EMA12':
                  value = data?.ema12;
                  break;
                case 'EMA26':
                  value = data?.ema26;
                  break;
                case 'RSI14':
                  value = data?.rsi14;
                  break;
                case 'TRENDLINE':
                  value = data?.trendLine;
                  break;
                case 'DYNAMIC_TREND':
                  value = data?.dynamicTrend;
                  break;
                case 'BB20':
                  if (data?.bbMiddle) {
                    return (
                      <div key={indicator} className="flex justify-between gap-4">
                        <span className="text-muted-foreground">BB:</span>
                        <span className="font-mono text-foreground text-xs">
                          {formatPrice(data?.bbUpper)} / {formatPrice(data?.bbMiddle)} / {formatPrice(data?.bbLower)}
                        </span>
                      </div>
                    );
                  }
                  break;
              }
              
              if (value !== null && value !== undefined) {
                return (
                  <div key={indicator} className="flex justify-between gap-4">
                    <span className="text-muted-foreground">{indicatorConfig?.name}:</span>
                    <span className="font-mono text-foreground" style={{ color: indicatorConfig?.color }}>
                      {indicator === 'RSI14' ? value?.toFixed(2) : formatPrice(value)}
                    </span>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`h-full bg-card flex flex-col ${isRTL ? 'rtl' : ''}`} style={{
      borderLeft: '2px dotted #ebeff4',
      borderBottom: '2px dotted #ebeff4'
    }}>
      {/* Chart Header */}
      <div className="flex items-center justify-between p-3 border-b border-border flex-wrap gap-2 flex-shrink-0">
        <div className="flex items-center space-x-3 min-w-0">
          <h3 className="text-lg font-semibold text-foreground">
            {currentSymbol?.symbol || 'EURUSD'}
          </h3>
          <div className="flex items-center space-x-2 text-sm">
            <span className="font-mono text-foreground">
              {formatPrice(currentSymbol?.bid)}
            </span>
            <span className={`font-mono ${
              (currentSymbol?.change || 0) >= 0 ? 'text-green-600' : 'text-red-600'
            }`}>
              {(currentSymbol?.change || 0) >= 0 ? '+' : ''}{(currentSymbol?.change || 0)?.toFixed(5)}
            </span>
          </div>
          
          {/* Connection Status */}
          <div className="flex items-center space-x-1">
            <Icon 
              name={getConnectionStatusIcon()} 
              size={14} 
              className={`${getConnectionStatusColor()} ${connectionStatus === 'connecting' ? 'animate-spin' : ''}`}
            />
            <span className={`text-xs ${getConnectionStatusColor()}`}>
              {t(connectionStatus)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Indicators Button */}
          <button
            onClick={() => setIndicatorPanelOpen(!indicatorPanelOpen)}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors flex items-center gap-2 ${
              indicatorPanelOpen
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
            }`}
          >
            <Icon name="BarChart3" size={14} />
            <span className="hidden sm:inline">{t('indicators')}</span>
          </button>

          {/* Timeframe Selector - Desktop */}
          <div className="hidden lg:flex items-center gap-1">
            {timeframes?.map((tf) => (
              <button
                key={tf}
                onClick={() => setChartTimeframe(tf)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap flex-shrink-0 min-w-[40px] ${
                  chartTimeframe === tf
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>

          {/* Timeframe Selector - Mobile Select */}
          <div className="lg:hidden min-w-[100px]">
            <Select
              value={chartTimeframe}
              onChange={setChartTimeframe}
              options={timeframeOptions}
              placeholder="Interval"
              className="text-xs"
            />
          </div>
        </div>
      </div>
      {/* Indicators Panel */}
      {indicatorPanelOpen && (
        <div className="p-3 border-b border-border bg-muted/50">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-foreground">{t('technicalIndicators')}</h4>
            <button
              onClick={() => setIndicatorPanelOpen(false)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="X" size={16} />
            </button>
          </div>
          
          {/* Responsive indicator grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
            {availableIndicators?.map((indicator) => (
              <button
                key={indicator?.key}
                onClick={() => toggleIndicator(indicator?.key)}
                className={`px-2 py-1.5 text-xs font-medium rounded transition-colors flex items-center justify-center gap-1.5 text-center ${
                  selectedIndicators?.includes(indicator?.key)
                    ? 'bg-primary/20 text-primary border-2 border-primary' :'text-muted-foreground hover:text-foreground hover:bg-muted border border-border'
                }`}
              >
                <div 
                  className="w-2 h-2 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: indicator?.color }}
                />
                <span className="truncate text-xs leading-tight">{indicator?.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Chart Area with Indicators */}
      <div className="flex-1 relative min-h-0">
        {chartLoaded && chartDataWithIndicators?.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartDataWithIndicators}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="#334155" 
                opacity={0.3}
              />
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 12, fill: '#9ca3af' }}
                axisLine={{ stroke: '#485563' }}
                tickLine={{ stroke: '#485563' }}
                interval="preserveStartEnd"
              />
              <YAxis 
                domain={['dataMin - 0.001', 'dataMax + 0.001']}
                tick={{ fontSize: 12, fill: '#9ca3af' }}
                axisLine={{ stroke: '#485563' }}
                tickLine={{ stroke: '#485563' }}
                tickFormatter={formatPrice}
                orientation="right"
              />
              
              {/* Render indicator lines */}
              {selectedIndicators?.map((indicator) => {
                const indicatorConfig = availableIndicators?.find(ind => ind?.key === indicator);
                
                switch (indicator) {
                  case 'SMA20':
                    return (
                      <Line
                        key={indicator}
                        type="monotone"
                        dataKey="sma20"
                        stroke={indicatorConfig?.color}
                        strokeWidth={1.5}
                        dot={false}
                        connectNulls={false}
                        strokeOpacity={0.8}
                      />
                    );
                  case 'SMA50':
                    return (
                      <Line
                        key={indicator}
                        type="monotone"
                        dataKey="sma50"
                        stroke={indicatorConfig?.color}
                        strokeWidth={1.5}
                        dot={false}
                        connectNulls={false}
                        strokeOpacity={0.8}
                      />
                    );
                  case 'EMA12':
                    return (
                      <Line
                        key={indicator}
                        type="monotone"
                        dataKey="ema12"
                        stroke={indicatorConfig?.color}
                        strokeWidth={1.5}
                        dot={false}
                        connectNulls={false}
                        strokeOpacity={0.8}
                      />
                    );
                  case 'EMA26':
                    return (
                      <Line
                        key={indicator}
                        type="monotone"
                        dataKey="ema26"
                        stroke={indicatorConfig?.color}
                        strokeWidth={1.5}
                        dot={false}
                        connectNulls={false}
                        strokeOpacity={0.8}
                      />
                    );
                  case 'TRENDLINE':
                    return (
                      <Line
                        key={indicator}
                        type="step"
                        dataKey="trendLine"
                        stroke={indicatorConfig?.color}
                        strokeWidth={2}
                        dot={false}
                        connectNulls={false}
                        strokeOpacity={0.9}
                        strokeDasharray="4 4"
                      />
                    );
                  case 'DYNAMIC_TREND':
                    return (
                      <Line
                        key={indicator}
                        type="step"
                        dataKey="dynamicTrend"
                        stroke={indicatorConfig?.color}
                        strokeWidth={2.5}
                        dot={false}
                        connectNulls={false}
                        strokeOpacity={1}
                      />
                    );
                  case 'BB20':
                    return (
                      <React.Fragment key={indicator}>
                        <Line
                          type="monotone"
                          dataKey="bbUpper"
                          stroke={indicatorConfig?.color}
                          strokeWidth={1}
                          dot={false}
                          connectNulls={false}
                          strokeOpacity={0.6}
                          strokeDasharray="2 2"
                        />
                        <Line
                          type="monotone"
                          dataKey="bbMiddle"
                          stroke={indicatorConfig?.color}
                          strokeWidth={1.5}
                          dot={false}
                          connectNulls={false}
                          strokeOpacity={0.8}
                        />
                        <Line
                          type="monotone"
                          dataKey="bbLower"
                          stroke={indicatorConfig?.color}
                          strokeWidth={1}
                          dot={false}
                          connectNulls={false}
                          strokeOpacity={0.6}
                          strokeDasharray="2 2"
                        />
                      </React.Fragment>
                    );
                  default:
                    return null;
                }
              })}

              {/* Custom Candlestick using Bar chart as base */}
              <ComposedChart>
                {chartDataWithIndicators?.map((entry, index) => (
                  <CustomCandlestick
                    key={index}
                    payload={{ payload: entry }}
                    x={index * (100 / chartDataWithIndicators?.length)} // Approximate positioning
                    y={0}
                    width={100 / chartDataWithIndicators?.length}
                    height={100}
                  />
                ))}
              </ComposedChart>

              {/* Tooltip */}
              <Tooltip content={<CustomTooltip />} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          /* Chart loading state */
          (<div className="absolute inset-0 flex items-center justify-center bg-background/50">
            <div className="text-center">
              <Icon name="TrendingUp" size={64} className="text-muted-foreground mb-4 mx-auto animate-pulse" />
              <p className="text-muted-foreground text-lg">
                {t('loadingChart') || 'Loading Candlestick Chart...'}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                {t('initializing') || 'Initializing'} {currentSymbol?.symbol || 'EURUSD'} - {chartTimeframe}
              </p>
              {connectionStatus === 'error' && (
                <p className="text-xs text-red-500 mt-1">
                  API connection failed - using simulated data
                </p>
              )}
            </div>
          </div>)
        )}

        {/* Chart Tools Overlay */}
        {chartLoaded && (
          <div className="absolute top-4 left-4 flex flex-col space-y-2">
            <button className="w-8 h-8 bg-card border border-border rounded flex items-center justify-center hover:bg-muted transition-colors">
              <Icon name="Plus" size={16} className="text-muted-foreground" />
            </button>
            <button className="w-8 h-8 bg-card border border-border rounded flex items-center justify-center hover:bg-muted transition-colors">
              <Icon name="Minus" size={16} className="text-muted-foreground" />
            </button>
            <button className="w-8 h-8 bg-card border border-border rounded flex items-center justify-center hover:bg-muted transition-colors">
              <Icon name="Maximize2" size={16} className="text-muted-foreground" />
            </button>
          </div>
        )}

        {/* RSI Indicator Panel (when RSI14 is selected) */}
        {selectedIndicators?.includes('RSI14') && chartLoaded && (
          <div className="absolute bottom-4 left-4 w-48 h-24 bg-card border border-border rounded-lg p-2">
            <div className="text-xs text-muted-foreground mb-1">RSI (14)</div>
            <div className="h-16 bg-muted/30 rounded relative">
              <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
                <div className="h-full w-full relative overflow-hidden">
                  {/* RSI levels */}
                  <div className="absolute top-0 left-0 w-full h-px bg-red-500/30"></div>
                  <div className="absolute top-1/4 left-0 w-full h-px bg-yellow-500/30"></div>
                  <div className="absolute top-2/4 left-0 w-full h-px bg-muted-foreground/30"></div>
                  <div className="absolute top-3/4 left-0 w-full h-px bg-yellow-500/30"></div>
                  <div className="absolute bottom-0 left-0 w-full h-px bg-red-500/30"></div>
                  
                  {/* Current RSI value */}
                  {chartDataWithIndicators?.slice(-1)?.[0]?.rsi14 && (
                    <div 
                      className="absolute w-2 h-2 bg-purple-500 rounded-full transform -translate-x-1 -translate-y-1"
                      style={{ 
                        left: '90%', 
                        top: `${100 - chartDataWithIndicators?.slice(-1)?.[0]?.rsi14}%` 
                      }}
                    />
                  )}
                  
                  <div className="absolute top-0 right-1 text-xs text-red-500">70</div>
                  <div className="absolute top-1/2 right-1 text-xs text-muted-foreground transform -translate-y-1/2">50</div>
                  <div className="absolute bottom-0 right-1 text-xs text-red-500">30</div>
                  
                  {/* Current RSI value display */}
                  <div className="absolute bottom-0 left-1 text-xs font-mono text-purple-600">
                    RSI: {chartDataWithIndicators?.slice(-1)?.[0]?.rsi14?.toFixed(2) || '--'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Trading Controls - Always Visible */}
      <div className="p-3 border-t border-border flex-shrink-0 min-h-[80px]">
        <div className="flex items-center justify-center space-x-4">
          <button 
            onClick={() => handleTradeClick('sell')}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 px-6 rounded-md font-medium flex items-center justify-center space-x-2 max-w-32 transition-colors"
          >
            <Icon name="TrendingDown" size={16} />
            <span>{t('sell')}</span>
          </button>
          <div className="flex flex-col items-center space-y-1 text-xs">
            <span className="text-muted-foreground">{t('spread')}</span>
            <span className="font-mono text-foreground">
              {((currentSymbol?.ask || 1.1676) - (currentSymbol?.bid || 1.1674))?.toFixed(5)}
            </span>
          </div>
          <button 
            onClick={() => handleTradeClick('buy')}
            className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-md font-medium flex items-center justify-center space-x-2 max-w-32 transition-colors"
          >
            <Icon name="TrendingUp" size={16} />
            <span>{t('buy')}</span>
          </button>
        </div>
      </div>
      {/* Trading Modal */}
      <TradingModal
        isOpen={tradingModalOpen}
        onClose={() => setTradingModalOpen(false)}
        type={tradingType}
        symbol={currentSymbol}
      />
    </div>
  );
};

export default TradingChart;